import React, { useState, useCallback } from "react";
import { Config } from "./MachineTaskConstants";
import { NotificationManager } from "react-notifications";
import fetchMSSQLHook from "../../../../hooks/fetchMSSQL-hook";
import { toAPI } from "./Task";

import { useModalState, useTasks } from "./hooks/useMachineTasks";

import ParameterChangeForm from "./components/ParameterChangeForm";
import ParameterGetForm from "./components/ParameterGetForm";
import ConfirmRestartForm from "./components/ConfirmRestartForm";
import ConfirmRecoverSettingsForm from "./components/ConfirmRecoverSettingsForm";
import TaskList from "./components/TaskList";
import ControlUI from "./components/ControlUI";
import ErrorBoundary from "./components/ErrorBoundary";
import TemperatureForm from "./components/TemperatureForm";

const MachineTaskView = ({ machineType, machineId }) => {
  const { fetchMssqlApi } = fetchMSSQLHook();
  const { modals, openModal, closeModal } = useModalState();
  const { 
    tasks: commandsTasks, 
    addTask, 
    removeTask, 
    clearAllTasks, 
    updateTaskDateTime, 
    updateTaskEveryNth,
    generateTaskId 
  } = useTasks();
  
  const [isExecuting, setIsExecuting] = useState(false);

  const MAX_TASKS = 3;

  const handleButtonClick = useCallback((e) => {
    if (commandsTasks.length >= MAX_TASKS) {
      NotificationManager.warning(`Maksymalna liczba jednorazowo zlecanych zadań wynosi ${MAX_TASKS}.`);
      return;
    }

    const buttonElement = e.target.closest("button") || e.target;
    const buttonId = buttonElement && buttonElement.id ? buttonElement.id : "";
    if (!buttonId) return;

    const [taskType, id] = buttonId.split("-");
    if (!taskType || taskType.trim() === "") return;

    const taskConfig = Config.find((config) => config.taskType === taskType);
    if (!taskConfig && !["PARAM_CHANGE", "GET_PARAMETER", "TEMPERATURE", "RESTART", "RECOVER_SETTINGS"].includes(taskType)) {
      return;
    }
    
    switch (taskType) {
      case "PARAM_CHANGE":
        openModal('changeParameter');
        break;
      case "GET_PARAMETER":
        openModal('getParameter');
        break;
      case "TEMPERATURE":
        openModal('temperature');
        break;
      case "RESTART":
        openModal('confirmRestart');
        break;
      case "RECOVER_SETTINGS":
        openModal('confirmRecoverSettings');
        break;
      default: {
        const task = {
          id: generateTaskId(),
          taskType,
          value: taskConfig && (taskConfig.options.length > 1 || id) ? id : null,
          datetimeType: taskConfig && taskConfig.datetimeType ? taskConfig.datetimeType : null,
          disableReccuring: taskConfig && taskConfig.disableReccuring ? taskConfig.disableReccuring : false,
        };
        addTask(task);
        break;
      }
    }
  }, [commandsTasks, openModal, addTask, generateTaskId]);

  const handleTaskExecution = useCallback(async () => {
    if (isExecuting || commandsTasks.length === 0) return;
    
    setIsExecuting(true);
    try {
      const formattedTasks = commandsTasks.map(task => toAPI(task)).flat();

      await new Promise((resolve, reject) => {
        fetchMssqlApi(
          "machine-tasks",
          {
            hideNotification: true,
            method: "POST",
            data: {
              machineId: machineId,
              tasks: formattedTasks,
              slaveNo: machineType,
            },
          },
          (res) => {
            if (res.statusCode === 200) {
              NotificationManager.success("Polecenia zostały wysłane do wykonania");
              clearAllTasks();
              resolve(res);
            } else {
              NotificationManager.error("Wystąpił błąd podczas wysyłania polecenia.");
              reject(new Error("API Error"));
            }
          }
        );
      });
    } catch (error) {
      console.error("Task execution failed:", error);
    } finally {
      setIsExecuting(false);
    }
  }, [isExecuting, commandsTasks, fetchMssqlApi, machineId, machineType, clearAllTasks]);

  const handleTaskSubmit = useCallback((task) => {
    if (commandsTasks.length >= MAX_TASKS) {
      NotificationManager.warning(`Maksymalna liczba jednorazowo zlecanych zadań wynosi ${MAX_TASKS}.`);
      return;
    }

    addTask(task);
    
    [ 'changeParameter',
      'getParameter',
      'confirmRestart',
      'confirmRecoverSettings',
      'temperature'
    ].map(closeModal)
  }, [commandsTasks, addTask, closeModal]);

  return (
    <ErrorBoundary>
      <div className="container-fluid">
        <div className="row w-100">
          <div className="col-12 mb-2">
            <ControlUI 
              onButtonClick={handleButtonClick}
              isExecuting={isExecuting}
              isMaxReached={commandsTasks.length >= MAX_TASKS}
            />
            
            <TaskList
              tasks={commandsTasks}
              onRemoveTask={removeTask}
              onUpdateDateTime={updateTaskDateTime}
              onUpdateEveryNth={updateTaskEveryNth}
              onExecute={handleTaskExecution}
              isExecuting={isExecuting}
            />

            <ParameterChangeForm
              isOpen={modals.changeParameter}
              onClose={() => closeModal('changeParameter')}
              onSubmit={handleTaskSubmit}
              generateTaskId={generateTaskId}
            />

            <ParameterGetForm
              isOpen={modals.getParameter}
              onClose={() => closeModal('getParameter')}
              onSubmit={handleTaskSubmit}
              generateTaskId={generateTaskId}
            />
            <ConfirmRestartForm
              isOpen={modals.confirmRestart}
              onClose={() => closeModal('confirmRestart')}
              onSubmit={handleTaskSubmit}
              generateTaskId={generateTaskId}
            />
            <ConfirmRecoverSettingsForm
              isOpen={modals.confirmRecoverSettings}
              onClose={() => closeModal('confirmRecoverSettings')}
              onSubmit={handleTaskSubmit}
              generateTaskId={generateTaskId}
            />
            <TemperatureForm
              isOpen={modals.temperature}
              onClose={() => closeModal('temperature')}
              onSubmit={handleTaskSubmit}
              onUpdateEveryNth={updateTaskEveryNth}
              generateTaskId={generateTaskId}
            />
          </div>
        </div>
      </div>
    </ErrorBoundary>
  );
};

export default MachineTaskView;